/**
 * Shared edge routing rules.
 * Computes decision exit points, back-edge routing, grazing avoidance,
 * and parallel edge separation. Writes to edge.meta.
 *
 * Used by both activity and flowchart renderers.
 * Direction-aware: supports both TD (top-down) and LR (left-right) layouts.
 */

import type { IRGraph } from '../types';
import { NodeShape } from '../types';

const GRAZE_MARGIN = 8;
const GRAZE_OFFSET = 20;
const PARALLEL_OFFSET = 15;
const BACK_EDGE_OFFSET = 30;

/**
 * Run all edge routing rules on a laid-out graph.
 * Skips laneless graphs (dagre already handles routing).
 */
export function applyEdgeRoutingRules(graph: IRGraph): void {
  if (graph.lanes.length === 0) return;
  applyDecisionExitPoints(graph);
  applyBackEdgeRouting(graph);
  applyGrazingAvoidance(graph);
  applyStraightLineGrazingAvoidance(graph);
  applyParallelEdgeSeparation(graph);
}

// ─── Helpers ────────────────────────────────────────────────────

interface AbsPos {
  absX: number;
  absY: number;
  width: number;
  height: number;
  laneId?: string;
}

function getNodeAbsPosition(graph: IRGraph, nodeId: string): AbsPos | null {
  const node = graph.nodes.get(nodeId);
  if (!node || node.x === undefined || node.y === undefined) return null;
  let laneX = 0;
  let laneY = 0;
  if (node.laneId) {
    const lane = graph.lanes.find((l) => l.id === node.laneId);
    if (lane) {
      laneX = lane.x;
      laneY = lane.y;
    }
  }
  return {
    absX: laneX + node.x,
    absY: laneY + node.y,
    width: node.width,
    height: node.height,
    laneId: node.laneId,
  };
}

/** Get the absolute center coordinate perpendicular to the flow direction */
function getNodeSortKey(graph: IRGraph, nodeId: string, isH: boolean): number {
  const pos = getNodeAbsPosition(graph, nodeId);
  if (!pos) return 0;
  return isH ? pos.absY + pos.height / 2 : pos.absX + pos.width / 2;
}

/**
 * Convert absolute coordinates to edge-parent-relative coordinates.
 * Intra-lane edges: subtract lane origin. Cross-lane edges: keep as-is.
 */
function toEdgeParentSpace(
  graph: IRGraph,
  edge: { crossLane?: boolean; source: string },
  absPoint: { x: number; y: number },
): { x: number; y: number } {
  if (edge.crossLane) {
    return absPoint;
  }
  const srcNode = graph.nodes.get(edge.source);
  if (srcNode?.laneId) {
    const lane = graph.lanes.find((l) => l.id === srcNode.laneId);
    if (lane) {
      return { x: absPoint.x - lane.x, y: absPoint.y - lane.y };
    }
  }
  return absPoint;
}

/** Check if a line segment intersects an axis-aligned box */
function segmentIntersectsBox(
  seg: { x1: number; y1: number; x2: number; y2: number },
  box: { left: number; right: number; top: number; bottom: number },
): boolean {
  const minX = Math.min(seg.x1, seg.x2);
  const maxX = Math.max(seg.x1, seg.x2);
  const minY = Math.min(seg.y1, seg.y2);
  const maxY = Math.max(seg.y1, seg.y2);

  if (maxX < box.left || minX > box.right) return false;
  if (maxY < box.top || minY > box.bottom) return false;

  // Horizontal segment
  if (Math.abs(seg.y1 - seg.y2) < 1) {
    return seg.y1 >= box.top && seg.y1 <= box.bottom
      && maxX >= box.left && minX <= box.right;
  }
  // Vertical segment
  if (Math.abs(seg.x1 - seg.x2) < 1) {
    return seg.x1 >= box.left && seg.x1 <= box.right
      && maxY >= box.top && minY <= box.bottom;
  }

  return true;
}

// ─── Rule 1: Decision exit points ───────────────────────────────

/** Assign exit points for decision node edges to prevent stacking */
function applyDecisionExitPoints(graph: IRGraph): void {
  const isH = graph.direction === 'LR';

  // Named exit points based on direction
  const forward = isH ? { x: 1, y: 0.5 } : { x: 0.5, y: 1 };
  const sideA = isH ? { x: 0.5, y: 0 } : { x: 0, y: 0.5 };
  const sideB = isH ? { x: 0.5, y: 1 } : { x: 1, y: 0.5 };

  // Group outgoing edges by source
  const outgoing = new Map<string, Array<{ source: string; target: string; idx: number }>>();
  for (let i = 0; i < graph.edges.length; i++) {
    const edge = graph.edges[i];
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
    outgoing.get(edge.source)!.push({ source: edge.source, target: edge.target, idx: i });
  }

  for (const [nid, node] of graph.nodes) {
    if (node.shape !== NodeShape.Decision) continue;
    const outs = outgoing.get(nid) ?? [];
    if (outs.length === 0) continue;

    // Sort by target's position perpendicular to flow
    const sorted = [...outs].sort((a, b) => {
      return getNodeSortKey(graph, a.target, isH) - getNodeSortKey(graph, b.target, isH);
    });

    const assign = (entry: { idx: number }, pt: { x: number; y: number }) => {
      const edge = graph.edges[entry.idx];
      if (!edge.meta) edge.meta = {};
      edge.meta.exitPoint = pt;
    };

    if (sorted.length === 1) {
      assign(sorted[0], forward);
    } else if (sorted.length === 2) {
      assign(sorted[0], sideA);
      assign(sorted[1], sideB);
    } else {
      assign(sorted[0], sideA);
      assign(sorted[1], sideB);
      for (let i = 2; i < sorted.length; i++) {
        assign(sorted[i], forward);
      }
    }
  }
}

// ─── Rule 2: Back-edge routing ──────────────────────────────────

/** Get lane bounds perpendicular to the flow direction */
function getLaneBounds(
  graph: IRGraph,
  laneId: string,
  isH: boolean,
): { min: number; max: number } | null {
  const lane = graph.lanes.find((l) => l.id === laneId);
  if (!lane) return null;
  if (isH) {
    return { min: lane.y, max: lane.y + lane.height };
  }
  return { min: lane.x, max: lane.x + lane.width };
}

/** Choose the less-congested side for a back-edge corridor */
function chooseSide(
  graph: IRGraph,
  edge: { source: string; target: string },
  srcPos: AbsPos,
  tgtPos: AbsPos,
  routedEdges: Array<{ routePoints: Array<{ x: number; y: number }> }>,
  isH: boolean,
): 'sideA' | 'sideB' {
  // In TD: sideA=left, sideB=right (corridor along X axis)
  // In LR: sideA=top, sideB=bottom (corridor along Y axis)

  let corridorA: number;
  let corridorB: number;
  let scanMin: number;
  let scanMax: number;

  if (isH) {
    corridorA = Math.min(srcPos.absY, tgtPos.absY) - BACK_EDGE_OFFSET;
    corridorB = Math.max(srcPos.absY + srcPos.height, tgtPos.absY + tgtPos.height) + BACK_EDGE_OFFSET;
    scanMin = Math.min(srcPos.absX, tgtPos.absX);
    scanMax = Math.max(srcPos.absX + srcPos.width, tgtPos.absX + tgtPos.width);
  } else {
    corridorA = Math.min(srcPos.absX, tgtPos.absX) - BACK_EDGE_OFFSET;
    corridorB = Math.max(srcPos.absX + srcPos.width, tgtPos.absX + tgtPos.width) + BACK_EDGE_OFFSET;
    scanMin = Math.min(srcPos.absY, tgtPos.absY);
    scanMax = Math.max(srcPos.absY + srcPos.height, tgtPos.absY + tgtPos.height);
  }

  let scoreA = 0;
  let scoreB = 0;

  // 1. Count node obstructions in each corridor
  for (const [nid] of graph.nodes) {
    if (nid === edge.source || nid === edge.target) continue;
    const nPos = getNodeAbsPosition(graph, nid);
    if (!nPos) continue;

    const box = {
      left: nPos.absX,
      right: nPos.absX + nPos.width,
      top: nPos.absY,
      bottom: nPos.absY + nPos.height,
    };

    if (isH) {
      // Horizontal corridors at Y=corridorA/corridorB, spanning from scanMin to scanMax in X
      if (segmentIntersectsBox({ x1: scanMin, y1: corridorA, x2: scanMax, y2: corridorA }, box)) {
        scoreA++;
      }
      if (segmentIntersectsBox({ x1: scanMin, y1: corridorB, x2: scanMax, y2: corridorB }, box)) {
        scoreB++;
      }
    } else {
      // Vertical corridors at X=corridorA/corridorB, spanning from scanMin to scanMax in Y
      if (segmentIntersectsBox({ x1: corridorA, y1: scanMin, x2: corridorA, y2: scanMax }, box)) {
        scoreA++;
      }
      if (segmentIntersectsBox({ x1: corridorB, y1: scanMin, x2: corridorB, y2: scanMax }, box)) {
        scoreB++;
      }
    }
  }

  // 2. Count other back-edges already routed on each side
  const CORRIDOR_TOLERANCE = 5;
  for (const routed of routedEdges) {
    for (const pt of routed.routePoints) {
      const coord = isH ? pt.y : pt.x;
      if (Math.abs(coord - corridorA) < CORRIDOR_TOLERANCE) { scoreA++; break; }
      if (Math.abs(coord - corridorB) < CORRIDOR_TOLERANCE) { scoreB++; break; }
    }
  }

  // 3. Lane boundary proximity penalty
  const laneId = srcPos.laneId || tgtPos.laneId;
  if (laneId) {
    const bounds = getLaneBounds(graph, laneId, isH);
    if (bounds) {
      if (corridorA < bounds.min) scoreA += 10;
      if (corridorB > bounds.max) scoreB += 10;
    }
  }

  return scoreA <= scoreB ? 'sideA' : 'sideB';
}

/** Route back-edges (loops) around the less-congested side */
function applyBackEdgeRouting(graph: IRGraph): void {
  const isH = graph.direction === 'LR';
  const routedEdges: Array<{ routePoints: Array<{ x: number; y: number }> }> = [];

  for (const edge of graph.edges) {
    if (!edge.meta?.isBackEdge) continue;

    const srcPos = getNodeAbsPosition(graph, edge.source);
    const tgtPos = getNodeAbsPosition(graph, edge.target);
    if (!srcPos || !tgtPos) continue;

    const side = chooseSide(graph, edge, srcPos, tgtPos, routedEdges, isH);

    if (!edge.meta) edge.meta = {};

    let routePoints: Array<{ x: number; y: number }>;

    if (isH) {
      // LR: corridor runs horizontally, exits top (sideA) or bottom (sideB)
      let corridorY: number;
      if (side === 'sideA') {
        edge.meta.exitPoint = { x: 0.5, y: 0 };
        edge.meta.entryPoint = { x: 0.5, y: 0 };
        corridorY = Math.min(srcPos.absY, tgtPos.absY) - BACK_EDGE_OFFSET;
      } else {
        edge.meta.exitPoint = { x: 0.5, y: 1 };
        edge.meta.entryPoint = { x: 0.5, y: 1 };
        corridorY = Math.max(srcPos.absY + srcPos.height, tgtPos.absY + tgtPos.height) + BACK_EDGE_OFFSET;
      }
      const srcCenterX = srcPos.absX + srcPos.width / 2;
      const tgtCenterX = tgtPos.absX + tgtPos.width / 2;
      routePoints = [
        toEdgeParentSpace(graph, edge, { x: srcCenterX, y: corridorY }),
        toEdgeParentSpace(graph, edge, { x: tgtCenterX, y: corridorY }),
      ];
    } else {
      // TD: corridor runs vertically, exits left (sideA) or right (sideB)
      let corridorX: number;
      if (side === 'sideA') {
        edge.meta.exitPoint = { x: 0, y: 0.5 };
        edge.meta.entryPoint = { x: 0, y: 0.5 };
        corridorX = Math.min(srcPos.absX, tgtPos.absX) - BACK_EDGE_OFFSET;
      } else {
        edge.meta.exitPoint = { x: 1, y: 0.5 };
        edge.meta.entryPoint = { x: 1, y: 0.5 };
        corridorX = Math.max(srcPos.absX + srcPos.width, tgtPos.absX + tgtPos.width) + BACK_EDGE_OFFSET;
      }
      const srcCenterY = srcPos.absY + srcPos.height / 2;
      const tgtCenterY = tgtPos.absY + tgtPos.height / 2;
      routePoints = [
        toEdgeParentSpace(graph, edge, { x: corridorX, y: srcCenterY }),
        toEdgeParentSpace(graph, edge, { x: corridorX, y: tgtCenterY }),
      ];
    }

    edge.meta.routePoints = routePoints;
    routedEdges.push({ routePoints });
  }
}

// ─── Rule 3: Grazing avoidance ──────────────────────────────────

/** Add waypoints to edges that would graze unrelated nodes */
function applyGrazingAvoidance(graph: IRGraph): void {
  const isH = graph.direction === 'LR';

  for (const edge of graph.edges) {
    if (edge.crossLane) continue;
    const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
    if (!exitPt) continue;
    if (edge.meta?.routePoints) continue;

    const srcPos = getNodeAbsPosition(graph, edge.source);
    const tgtPos = getNodeAbsPosition(graph, edge.target);
    if (!srcPos || !tgtPos) continue;

    const srcCenterX = srcPos.absX + srcPos.width / 2;
    const srcCenterY = srcPos.absY + srcPos.height / 2;
    const tgtCenterX = tgtPos.absX + tgtPos.width / 2;
    const tgtCenterY = tgtPos.absY + tgtPos.height / 2;

    // Determine if this is a "side" exit (perpendicular to flow) or "forward" exit
    const isSideExit = isH
      ? (exitPt.x === 0.5 && (exitPt.y === 0 || exitPt.y === 1))  // top/bottom in LR
      : (exitPt.y === 0.5 && (exitPt.x === 0 || exitPt.x === 1)); // left/right in TD

    let segments: Array<{ x1: number; y1: number; x2: number; y2: number }>;

    if (isSideExit) {
      if (isH) {
        // LR side exit (top/bottom): horizontal leg then vertical leg
        const exitY = exitPt.y === 0 ? srcPos.absY : srcPos.absY + srcPos.height;
        segments = [
          { x1: srcCenterX, y1: exitY, x2: srcCenterX, y2: tgtCenterY },
          { x1: srcCenterX, y1: tgtCenterY, x2: tgtCenterX, y2: tgtCenterY },
        ];
      } else {
        // TD side exit (left/right): horizontal leg then vertical leg
        const exitX = exitPt.x === 0 ? srcPos.absX : srcPos.absX + srcPos.width;
        segments = [
          { x1: exitX, y1: srcCenterY, x2: tgtCenterX, y2: srcCenterY },
          { x1: tgtCenterX, y1: srcCenterY, x2: tgtCenterX, y2: tgtCenterY },
        ];
      }
    } else {
      if (isH) {
        // LR forward exit (right side): horizontal leg then vertical leg
        segments = [
          { x1: srcPos.absX + srcPos.width, y1: srcCenterY, x2: tgtCenterX, y2: srcCenterY },
          { x1: tgtCenterX, y1: srcCenterY, x2: tgtCenterX, y2: tgtCenterY },
        ];
      } else {
        // TD forward exit (bottom): vertical leg then horizontal leg
        segments = [
          { x1: srcCenterX, y1: srcPos.absY + srcPos.height, x2: srcCenterX, y2: tgtCenterY },
          { x1: srcCenterX, y1: tgtCenterY, x2: tgtCenterX, y2: tgtCenterY },
        ];
      }
    }

    // Collect ALL non-source, non-target nodes that graze the path
    const grazeNodes: AbsPos[] = [];
    for (const [nid] of graph.nodes) {
      if (nid === edge.source || nid === edge.target) continue;
      const nPos = getNodeAbsPosition(graph, nid);
      if (!nPos) continue;

      const box = {
        left: nPos.absX - GRAZE_MARGIN,
        right: nPos.absX + nPos.width + GRAZE_MARGIN,
        top: nPos.absY - GRAZE_MARGIN,
        bottom: nPos.absY + nPos.height + GRAZE_MARGIN,
      };

      for (const seg of segments) {
        if (segmentIntersectsBox(seg, box)) {
          grazeNodes.push(nPos);
          break;
        }
      }
    }

    if (grazeNodes.length > 0) {
      if (!edge.meta) edge.meta = {};

      if (isSideExit) {
        if (isH) {
          // LR side exit: route through horizontal corridor above/below obstructions
          let corridorY: number;
          if (exitPt.y === 1) {
            const maxBottom = Math.max(...grazeNodes.map((n) => n.absY + n.height));
            corridorY = maxBottom + GRAZE_OFFSET;
          } else {
            const minTop = Math.min(...grazeNodes.map((n) => n.absY));
            corridorY = minTop - GRAZE_OFFSET;
          }
          edge.meta.routePoints = [
            toEdgeParentSpace(graph, edge, { x: srcCenterX, y: corridorY }),
            toEdgeParentSpace(graph, edge, { x: tgtCenterX, y: corridorY }),
          ];
        } else {
          // TD side exit: route through vertical corridor left/right of obstructions
          let corridorX: number;
          if (exitPt.x === 1) {
            const maxRight = Math.max(...grazeNodes.map((n) => n.absX + n.width));
            corridorX = maxRight + GRAZE_OFFSET;
          } else {
            const minLeft = Math.min(...grazeNodes.map((n) => n.absX));
            corridorX = minLeft - GRAZE_OFFSET;
          }
          edge.meta.routePoints = [
            toEdgeParentSpace(graph, edge, { x: corridorX, y: srcCenterY }),
            toEdgeParentSpace(graph, edge, { x: corridorX, y: tgtCenterY }),
          ];
        }
      } else {
        // Forward exit: jog perpendicular to avoid
        const grazeNode = grazeNodes[0];
        if (isH) {
          const detourX = (srcCenterX < grazeNode.absX + grazeNode.width / 2)
            ? grazeNode.absX - GRAZE_OFFSET
            : grazeNode.absX + grazeNode.width + GRAZE_OFFSET;
          const detourY = (srcCenterY < grazeNode.absY + grazeNode.height / 2)
            ? grazeNode.absY - GRAZE_OFFSET
            : grazeNode.absY + grazeNode.height + GRAZE_OFFSET;
          edge.meta.routePoints = [
            toEdgeParentSpace(graph, edge, { x: detourX, y: detourY }),
            toEdgeParentSpace(graph, edge, { x: tgtCenterX, y: detourY }),
          ];
        } else {
          const detourY = (srcCenterY < grazeNode.absY + grazeNode.height / 2)
            ? grazeNode.absY - GRAZE_OFFSET
            : grazeNode.absY + grazeNode.height + GRAZE_OFFSET;
          const detourX = (srcCenterX < grazeNode.absX + grazeNode.width / 2)
            ? grazeNode.absX - GRAZE_OFFSET
            : grazeNode.absX + grazeNode.width + GRAZE_OFFSET;
          edge.meta.routePoints = [
            toEdgeParentSpace(graph, edge, { x: detourX, y: detourY }),
            toEdgeParentSpace(graph, edge, { x: tgtCenterX, y: detourY }),
          ];
        }
      }
    }
  }
}

// ─── Rule 3b: Straight-line grazing avoidance ───────────────────

/** Choose detour side for a straight-line edge avoiding obstructions */
function chooseStraightLineDetourSide(
  graph: IRGraph,
  edge: { source: string; target: string },
  obstructionBox: { left: number; right: number; top: number; bottom: number },
  srcPos: AbsPos,
  tgtPos: AbsPos,
  isH: boolean,
): 'sideA' | 'sideB' {
  // sideA = left/top of obstructions, sideB = right/bottom
  let corridorA: number;
  let corridorB: number;
  let scanSeg1: { x1: number; y1: number; x2: number; y2: number };
  let scanSeg2: { x1: number; y1: number; x2: number; y2: number };

  if (isH) {
    // LR: detour up (sideA) or down (sideB)
    corridorA = obstructionBox.top - GRAZE_OFFSET;
    corridorB = obstructionBox.bottom + GRAZE_OFFSET;
    const scanMin = Math.min(srcPos.absX, tgtPos.absX);
    const scanMax = Math.max(srcPos.absX + srcPos.width, tgtPos.absX + tgtPos.width);
    scanSeg1 = { x1: scanMin, y1: corridorA, x2: scanMax, y2: corridorA };
    scanSeg2 = { x1: scanMin, y1: corridorB, x2: scanMax, y2: corridorB };
  } else {
    // TD: detour left (sideA) or right (sideB)
    corridorA = obstructionBox.left - GRAZE_OFFSET;
    corridorB = obstructionBox.right + GRAZE_OFFSET;
    const scanMin = Math.min(srcPos.absY, tgtPos.absY);
    const scanMax = Math.max(srcPos.absY + srcPos.height, tgtPos.absY + tgtPos.height);
    scanSeg1 = { x1: corridorA, y1: scanMin, x2: corridorA, y2: scanMax };
    scanSeg2 = { x1: corridorB, y1: scanMin, x2: corridorB, y2: scanMax };
  }

  let scoreA = 0;
  let scoreB = 0;

  // Count node obstructions in each candidate corridor
  for (const [nid] of graph.nodes) {
    if (nid === edge.source || nid === edge.target) continue;
    const nPos = getNodeAbsPosition(graph, nid);
    if (!nPos) continue;
    const box = {
      left: nPos.absX,
      right: nPos.absX + nPos.width,
      top: nPos.absY,
      bottom: nPos.absY + nPos.height,
    };
    if (segmentIntersectsBox(scanSeg1, box)) scoreA++;
    if (segmentIntersectsBox(scanSeg2, box)) scoreB++;
  }

  // Lane boundary penalty
  const laneId = srcPos.laneId || tgtPos.laneId;
  if (laneId) {
    const bounds = getLaneBounds(graph, laneId, isH);
    if (bounds) {
      if (isH) {
        if (corridorA < bounds.min) scoreA += 10;
        if (corridorB > bounds.max) scoreB += 10;
      } else {
        if (corridorA < bounds.min) scoreA += 10;
        if (corridorB > bounds.max) scoreB += 10;
      }
    }
  }

  return scoreA <= scoreB ? 'sideA' : 'sideB';
}

/** Add waypoints to straight-line edges that pass through unrelated nodes */
function applyStraightLineGrazingAvoidance(graph: IRGraph): void {
  const isH = graph.direction === 'LR';

  for (const edge of graph.edges) {
    // Skip edges already handled by Rules 1-3
    if (edge.crossLane) continue;
    if (edge.meta?.routePoints) continue;
    if (edge.meta?.exitPoint) continue;
    if (edge.meta?.isBackEdge) continue;

    const srcPos = getNodeAbsPosition(graph, edge.source);
    const tgtPos = getNodeAbsPosition(graph, edge.target);
    if (!srcPos || !tgtPos) continue;

    const srcCenterX = srcPos.absX + srcPos.width / 2;
    const srcCenterY = srcPos.absY + srcPos.height / 2;
    const tgtCenterX = tgtPos.absX + tgtPos.width / 2;
    const tgtCenterY = tgtPos.absY + tgtPos.height / 2;

    // Straight-line segment from source center to target center
    const seg = { x1: srcCenterX, y1: srcCenterY, x2: tgtCenterX, y2: tgtCenterY };

    // Collect all obstructing nodes
    const grazeNodes: AbsPos[] = [];
    for (const [nid] of graph.nodes) {
      if (nid === edge.source || nid === edge.target) continue;
      const nPos = getNodeAbsPosition(graph, nid);
      if (!nPos) continue;

      const box = {
        left: nPos.absX - GRAZE_MARGIN,
        right: nPos.absX + nPos.width + GRAZE_MARGIN,
        top: nPos.absY - GRAZE_MARGIN,
        bottom: nPos.absY + nPos.height + GRAZE_MARGIN,
      };

      if (segmentIntersectsBox(seg, box)) {
        grazeNodes.push(nPos);
      }
    }

    if (grazeNodes.length === 0) continue;

    // Compute bounding box of all obstructions
    const obsBBox = {
      left: Math.min(...grazeNodes.map((n) => n.absX)),
      right: Math.max(...grazeNodes.map((n) => n.absX + n.width)),
      top: Math.min(...grazeNodes.map((n) => n.absY)),
      bottom: Math.max(...grazeNodes.map((n) => n.absY + n.height)),
    };

    const side = chooseStraightLineDetourSide(graph, edge, obsBBox, srcPos, tgtPos, isH);

    if (!edge.meta) edge.meta = {};

    if (isH) {
      // LR: detour up or down (perpendicular = Y axis)
      const corridorY = side === 'sideA'
        ? obsBBox.top - GRAZE_OFFSET
        : obsBBox.bottom + GRAZE_OFFSET;
      edge.meta.routePoints = [
        toEdgeParentSpace(graph, edge, { x: srcCenterX, y: corridorY }),
        toEdgeParentSpace(graph, edge, { x: tgtCenterX, y: corridorY }),
      ];
    } else {
      // TD: detour left or right (perpendicular = X axis)
      const corridorX = side === 'sideA'
        ? obsBBox.left - GRAZE_OFFSET
        : obsBBox.right + GRAZE_OFFSET;
      edge.meta.routePoints = [
        toEdgeParentSpace(graph, edge, { x: corridorX, y: srcCenterY }),
        toEdgeParentSpace(graph, edge, { x: corridorX, y: tgtCenterY }),
      ];
    }
  }
}

// ─── Rule 4: Parallel edge separation ───────────────────────────

/** Stagger exit points for parallel edges from the same decision to different targets */
function applyParallelEdgeSeparation(graph: IRGraph): void {
  const isH = graph.direction === 'LR';

  // Group outgoing non-back-edges by source
  const outgoing = new Map<string, number[]>();
  for (let i = 0; i < graph.edges.length; i++) {
    const edge = graph.edges[i];
    if (edge.meta?.isBackEdge) continue;
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
    outgoing.get(edge.source)!.push(i);
  }

  for (const [, indices] of outgoing) {
    if (indices.length < 2) continue;

    // Group by exit point
    const byExit = new Map<string, number[]>();
    for (const idx of indices) {
      const edge = graph.edges[idx];
      const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
      if (!exitPt) continue;
      const key = `${exitPt.x},${exitPt.y}`;
      if (!byExit.has(key)) byExit.set(key, []);
      byExit.get(key)!.push(idx);
    }

    for (const [exitKey, group] of byExit) {
      const uniqueTargets = new Set(group.map((i) => graph.edges[i].target));
      if (uniqueTargets.size < 2) continue;
      if (group.length < 2) continue;

      const [exitXStr, exitYStr] = exitKey.split(',');
      const exitX = parseFloat(exitXStr);
      const exitY = parseFloat(exitYStr);

      // Determine if this is a "side" exit
      const isSide = isH
        ? (exitX === 0.5 && (exitY === 0 || exitY === 1))  // top/bottom in LR
        : (exitY === 0.5 && (exitX === 0 || exitX === 1)); // left/right in TD

      if (isSide) {
        // Side exits: stagger along the flow axis
        if (isH) {
          // LR side (top/bottom): stagger X
          const step = 0.2 / (group.length - 1);
          for (let i = 0; i < group.length; i++) {
            const edge = graph.edges[group[i]];
            if (!edge.meta) edge.meta = {};
            const offset = -0.1 + step * i;
            (edge.meta.exitPoint as { x: number; y: number }).x = 0.5 + offset;
          }
        } else {
          // TD side (left/right): stagger Y
          const step = 0.1 / (group.length - 1);
          for (let i = 0; i < group.length; i++) {
            const edge = graph.edges[group[i]];
            if (!edge.meta) edge.meta = {};
            const offset = -0.05 + step * i;
            (edge.meta.exitPoint as { x: number; y: number }).y = 0.5 + offset;
          }
        }
      } else {
        // Forward exits: stagger perpendicular to flow
        if (isH) {
          // LR forward (right): stagger Y
          const step = 0.1 / (group.length - 1);
          for (let i = 0; i < group.length; i++) {
            const edge = graph.edges[group[i]];
            if (!edge.meta) edge.meta = {};
            const offset = -0.05 + step * i;
            (edge.meta.exitPoint as { x: number; y: number }).y = 0.5 + offset;
          }
        } else {
          // TD forward (bottom): stagger X
          const step = 0.2 / (group.length - 1);
          for (let i = 0; i < group.length; i++) {
            const edge = graph.edges[group[i]];
            if (!edge.meta) edge.meta = {};
            const offset = -0.1 + step * i;
            (edge.meta.exitPoint as { x: number; y: number }).x = 0.5 + offset;
          }
        }
      }

      // Add jog waypoint to second edge if paths would share corridor
      if (group.length === 2) {
        const edgeA = graph.edges[group[0]];
        const edgeB = graph.edges[group[1]];
        const posA = getNodeAbsPosition(graph, edgeA.target);
        const posB = getNodeAbsPosition(graph, edgeB.target);
        if (posA && posB && !edgeB.meta?.routePoints) {
          const proximity = isH
            ? Math.abs((posA.absY + posA.height / 2) - (posB.absY + posB.height / 2))
            : Math.abs((posA.absX + posA.width / 2) - (posB.absX + posB.width / 2));

          if (proximity < 100) {
            const srcPos = getNodeAbsPosition(graph, edgeB.source);
            if (srcPos) {
              if (!edgeB.meta) edgeB.meta = {};
              if (isH) {
                const jogX = srcPos.absX + srcPos.width + PARALLEL_OFFSET;
                edgeB.meta.routePoints = [
                  toEdgeParentSpace(graph, edgeB, {
                    x: jogX,
                    y: posB.absY + posB.height / 2 + PARALLEL_OFFSET,
                  }),
                ];
              } else {
                const jogY = srcPos.absY + srcPos.height + PARALLEL_OFFSET;
                edgeB.meta.routePoints = [
                  toEdgeParentSpace(graph, edgeB, {
                    x: posB.absX + posB.width / 2 + PARALLEL_OFFSET,
                    y: jogY,
                  }),
                ];
              }
            }
          }
        }
      }
    }
  }
}
